import React, { useState } from 'react';
import { Calculator, Info, DollarSign } from 'lucide-react';
import { calculateTax } from './utils/taxCalculator';
import {
  CURRENT_NZ_TAX_YEAR,
  defaultKiwiSaverRatePercent,
  formatNzDollars,
  formatTaxRate,
  incomeTaxBracketRows,
} from './utils/currentNzTaxYear';

interface PayPeriodResults {
  hourly: number;
  weekly: number;
  fortnightly: number;
  monthly: number;
  yearly: number;
}

interface Results {
  grossPay: PayPeriodResults;
  paye: PayPeriodResults;
  acc: PayPeriodResults;
  kiwiSaver: PayPeriodResults;
  studentLoan: PayPeriodResults;
  takeHomePay: PayPeriodResults;
}

function App() {
  const [income, setIncome] = useState<string>('');
  const [period, setPeriod] = useState<'annual' | 'monthly' | 'fortnightly' | 'weekly' | 'hourly'>('annual');
  const [kiwiSaver, setKiwiSaver] = useState<string>(String(defaultKiwiSaverRatePercent()));
  const [taxCode, setTaxCode] = useState<string>('M');
  const [hasStudentLoan, setHasStudentLoan] = useState(false);
  const [hoursWorked, setHoursWorked] = useState<string>('40');
  const [results, setResults] = useState<Results | null>(null);

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    let annualIncome = Number(income);
    
    // Convert income to annual based on period
    if (period === 'monthly') annualIncome *= 12;
    else if (period === 'fortnightly') annualIncome *= 26;
    else if (period === 'weekly') annualIncome *= 52;
    else if (period === 'hourly') annualIncome = Number(hoursWorked) * Number(income) * 52;

    const yearlyResults = calculateTax({
      salary: annualIncome,
      period: 'yearly',
      kiwiSaverRate: Number(kiwiSaver),
      hasStudentLoan: hasStudentLoan || taxCode.includes('SL'),
    });

    // Calculate for different periods
    const hoursPerWeek = Number(hoursWorked);
    const hoursPerYear = hoursPerWeek * 52;

    const convertToAllPeriods = (yearlyAmount: number): PayPeriodResults => ({
      hourly: yearlyAmount / hoursPerYear,
      weekly: yearlyAmount / 52,
      fortnightly: yearlyAmount / 26,
      monthly: yearlyAmount / 12,
      yearly: yearlyAmount,
    });

    setResults({
      grossPay: convertToAllPeriods(yearlyResults.grossPay),
      paye: convertToAllPeriods(yearlyResults.paye),
      acc: convertToAllPeriods(yearlyResults.acc),
      kiwiSaver: convertToAllPeriods(yearlyResults.kiwiSaver),
      studentLoan: convertToAllPeriods(yearlyResults.studentLoan),
      takeHomePay: convertToAllPeriods(yearlyResults.takeHomePay),
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const taxYear = CURRENT_NZ_TAX_YEAR;
  const taxYearLabel = taxYear.label;
  const accRateLabel = formatTaxRate(taxYear.accEarnersLevy.rate);
  const accCapLabel = formatNzDollars(taxYear.accEarnersLevy.maxLiableEarnings);
  const employerKiwiSaverMinimum = taxYear.kiwiSaver.employerMinimumRatePercent;
  const defaultKiwiSaverLabel = `${defaultKiwiSaverRatePercent()}%`;
  const otherKiwiSaverRates = taxYear.kiwiSaver.employeeRateOptions
    .filter((option) => option.percent > 0 && !option.isDefault && !option.isTemporaryReduction)
    .map((option) => `${option.percent}%`);
  const otherKiwiSaverRatesLabel =
    otherKiwiSaverRates.length > 1
      ? `${otherKiwiSaverRates.slice(0, -1).join(', ')}, or ${otherKiwiSaverRates[otherKiwiSaverRates.length - 1]}`
      : otherKiwiSaverRates.join('');
  const temporaryKiwiSaverRate = taxYear.kiwiSaver.employeeRateOptions.find(
    (option) => option.isTemporaryReduction,
  )?.percent;
  const taxBrackets = incomeTaxBracketRows();

  return (
    <div className="min-h-screen bg-[#f5f7fa]">
      <header className="bg-[#2c3e50] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center">
            <Calculator className="h-8 w-8 text-[#3498db]" />
            <h1 className="ml-2 text-2xl font-bold">Free<span className="text-[#3498db]">PAYE</span>Calculator.co.nz</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <section className="text-center mb-12">
          <h1 className="text-4xl font-bold text-[#2c3e50] mb-4">Free New Zealand PAYE Calculator</h1>
          <p className="text-lg text-[#34495e] max-w-3xl mx-auto">
            Calculate your take-home pay accurately with our free NZ PAYE calculator. Account for income tax, KiwiSaver contributions, ACC levies, and student loan deductions. Updated for the {taxYearLabel} tax year.
          </p>
        </section>

        <section id="calculator" className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <h2 className="text-2xl font-semibold text-[#2c3e50] text-center mb-6">PAYE Calculator NZ</h2>
          
          <form onSubmit={handleCalculate} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gross Income</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={income}
                  onChange={(e) => setIncome(e.target.value)}
                  className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#3498db] focus:border-[#3498db]"
                  placeholder="Enter amount"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pay Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as any)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#3498db] focus:border-[#3498db]"
              >
                <option value="annual">Annual</option>
                <option value="monthly">Monthly</option>
                <option value="fortnightly">Fortnightly</option>
                <option value="weekly">Weekly</option>
                <option value="hourly">Hourly</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">KiwiSaver Rate</label>
              <select
                value={kiwiSaver}
                onChange={(e) => setKiwiSaver(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#3498db] focus:border-[#3498db]"
              >
                {taxYear.kiwiSaver.employeeRateOptions.map((option) => (
                  <option key={option.percent} value={option.percent}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tax Code</label>
              <select
                value={taxCode}
                onChange={(e) => setTaxCode(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#3498db] focus:border-[#3498db]"
              >
                <option value="M">M (Standard)</option>
                <option value="ME">ME (Standard with IETC)</option>
                <option value="SL">M SL (With Student Loan)</option>
                <option value="SLME">ME SL (With IETC & Student Loan)</option>
                <option value="S">S (Secondary Job)</option>
                <option value="SH">SH (Secondary Higher Rate)</option>
                <option value="ST">ST (Secondary Higher + Student Loan)</option>
              </select>
            </div>

            {period === 'hourly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Hours Worked per Week</label>
                <input
                  type="number"
                  value={hoursWorked}
                  onChange={(e) => setHoursWorked(e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-[#3498db] focus:border-[#3498db]"
                  placeholder="40"
                />
              </div>
            )}

            <div className="md:col-span-2">
              <button
                type="submit"
                className="w-full bg-[#3498db] text-white py-2 px-4 rounded-md hover:bg-[#2980b9] transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#3498db]"
              >
                Calculate
              </button>
            </div>
          </form>

          {results && (
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-semibold text-gray-500"></th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-500">Hour</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-500">Week</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-500">Fortnight</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-500">Month</th>
                      <th className="px-6 py-3 text-right text-sm font-semibold text-gray-500">Year</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">Gross Pay</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.grossPay.hourly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.grossPay.weekly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.grossPay.fortnightly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.grossPay.monthly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.grossPay.yearly)}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">PAYE</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.paye.hourly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.paye.weekly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.paye.fortnightly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.paye.monthly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.paye.yearly)}</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">ACC</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.acc.hourly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.acc.weekly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.acc.fortnightly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.acc.monthly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.acc.yearly)}</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">KiwiSaver</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.kiwiSaver.hourly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.kiwiSaver.weekly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.kiwiSaver.fortnightly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.kiwiSaver.monthly)}</td>
                      <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.kiwiSaver.yearly)}</td>
                    </tr>
                    {(hasStudentLoan || taxCode.includes('SL')) && (
                      <tr className="bg-gray-50">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">Student Loan</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.studentLoan.hourly)}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.studentLoan.weekly)}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.studentLoan.fortnightly)}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.studentLoan.monthly)}</td>
                        <td className="px-6 py-4 text-right text-sm text-gray-500">{formatCurrency(results.studentLoan.yearly)}</td>
                      </tr>
                    )}
                    <tr className="bg-[#3498db] text-white">
                      <td className="px-6 py-4 text-sm font-medium">Take Home Pay</td>
                      <td className="px-6 py-4 text-right text-sm font-medium">{formatCurrency(results.takeHomePay.hourly)}</td>
                      <td className="px-6 py-4 text-right text-sm font-medium">{formatCurrency(results.takeHomePay.weekly)}</td>
                      <td className="px-6 py-4 text-right text-sm font-medium">{formatCurrency(results.takeHomePay.fortnightly)}</td>
                      <td className="px-6 py-4 text-right text-sm font-medium">{formatCurrency(results.takeHomePay.monthly)}</td>
                      <td className="px-6 py-4 text-right text-sm font-medium">{formatCurrency(results.takeHomePay.yearly)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>

        <section id="about" className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <div className="flex items-center mb-4">
            <Info className="h-6 w-6 text-[#3498db]" />
            <h2 className="ml-2 text-2xl font-semibold text-[#2c3e50]">Understanding PAYE in New Zealand</h2>
          </div>
          
          <div className="prose max-w-none">
            <p>PAYE (Pay As You Earn) is the system New Zealand uses to collect income tax and other deductions from employees' wages. Understanding how PAYE works is essential for both employers and employees to ensure correct tax payments and compliance with New Zealand tax laws.</p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-[#3498db] mb-2">What is PAYE?</h3>
                <p>PAYE stands for "Pay As You Earn" - it's the system where employers deduct tax from employees' wages before paying them. This means income tax is collected throughout the year rather than as a lump sum.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-[#3498db] mb-2">KiwiSaver</h3>
                <p>KiwiSaver is a voluntary work-based savings scheme. For the {taxYearLabel} tax year, employee contributions are deducted from gross pay at a default rate of {defaultKiwiSaverLabel}, or at {otherKiwiSaverRatesLabel}. Employees with a temporary rate reduction can contribute {temporaryKiwiSaverRate}%. Employers contribute at least {employerKiwiSaverMinimum}%.</p>
              </div>
              
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-[#3498db] mb-2">ACC Levies</h3>
                <p>The ACC earners' levy is a mandatory contribution that covers the cost of accidents and injuries. For the {taxYearLabel} tax year the rate is {accRateLabel} of your gross earnings (GST inclusive), on earnings up to ${accCapLabel}.</p>
              </div>
            </div>
          </div>
        </section>

        <section id="tax-rates" className="bg-white rounded-lg shadow-lg p-6 mb-12">
          <h2 className="text-2xl font-semibold text-[#2c3e50] mb-6">New Zealand Income Tax Rates ({taxYearLabel})</h2>
          <p className="mb-6 text-[#34495e]">Rates for the {taxYearLabel} tax year ({taxYear.periodLabel}).</p>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr className="bg-[#2c3e50] text-white">
                  <th className="px-6 py-3 text-left text-sm font-semibold">Income Bracket</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Tax Rate</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {taxBrackets.map((bracket, index) => (
                  <tr key={bracket.range} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4">{bracket.range}</td>
                    <td className="px-6 py-4">{bracket.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section id="faq" className="bg-white rounded-lg shadow-lg p-6">
          <h2 className="text-2xl font-semibold text-[#2c3e50] mb-6">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">What is the difference between gross pay and net pay?</h3>
              <p>Gross pay is your total earnings before any deductions. Net pay (take-home pay) is what you receive after all deductions like PAYE tax, KiwiSaver, ACC levy, and student loan repayments.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">How is KiwiSaver calculated?</h3>
              <p>KiwiSaver contributions are calculated as a percentage of your gross pay. For the {taxYearLabel} tax year you can contribute {defaultKiwiSaverLabel} (the default), {otherKiwiSaverRatesLabel}, or {temporaryKiwiSaverRate}% if you have a temporary rate reduction. Your employer must contribute at least {employerKiwiSaverMinimum}% on top of your salary.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-[#2c3e50] mb-2">What is ACC and why do I pay it?</h3>
              <p>ACC provides comprehensive, no-fault personal injury cover for all New Zealand residents and visitors. For the {taxYearLabel} tax year the earners' levy is {accRateLabel} of your gross earnings (GST inclusive), on earnings up to ${accCapLabel}.</p>
            </div>
          </div>
        </section>
      </main>

      <footer className="bg-[#2c3e50] text-white mt-12">
        <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div className="text-center">
            <p>&copy; 2026 Free PAYE Calculator NZ. All rights reserved.</p>
            <p className="mt-2">Disclaimer: This calculator provides estimates only. For official tax advice, please consult a qualified tax professional or Inland Revenue.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;